import { useMemo } from "react";
import { useAdminBookings, useAdminPayments, money, fmtDateTime, occupies, downloadCsv, STATUS } from "@/lib/booking";
import { Card, PageTitle, SubTitle, Notice, smallBtn } from "./adminUI";
import MercadoPagoAdmin from "./MercadoPagoAdmin";

function Stat({ label, value, testid }) {
    return <Card className="text-center"><p className="font-display text-2xl font-black text-neon-cyan" data-testid={testid}>{value}</p>
        <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.2em] text-dim">{label}</p></Card>;
}

export default function PaymentsAdmin() {
    const payments = useAdminPayments();
    const bookings = useAdminBookings();
    const byId = useMemo(() => Object.fromEntries((bookings.data || []).map((b) => [b.id, b])), [bookings.data]);
    const rows = payments.data || [];
    const sum = (k) => rows.filter((p) => p.kind === k).reduce((s, p) => s + Number(p.amount), 0);
    const pending = (bookings.data || []).filter((b) => occupies(b) && Number(b.total) - Number(b.paid_amount) > 0);

    const exportPayments = () => downloadCsv("pagamentos.csv", [["data", "reserva", "cliente", "tipo", "metodo", "valor", "obs"],
        ...rows.map((p) => [fmtDateTime(p.created_at), byId[p.booking_id]?.code, byId[p.booking_id]?.customer_name, p.kind, p.method, Number(p.amount).toFixed(2), p.note])]);
    const exportBookings = () => downloadCsv("reservas.csv", [["codigo", "criada", "cliente", "status", "total", "pago", "reembolsado", "saldo"],
        ...(bookings.data || []).map((b) => [b.code, fmtDateTime(b.created_at), b.customer_name, STATUS[b.status], b.total, b.paid_amount, b.refunded_amount, Math.max(0, b.total - b.paid_amount).toFixed(2)])]);

    return (
        <div className="space-y-6">
            <PageTitle title="Pagamentos">Acompanhe pagamentos manuais, saldos e cobranças por Mercado Pago gerenciadas no Supabase.</PageTitle>
            <MercadoPagoAdmin />
            {payments.isError && <Notice tone="amber">Sem acesso aos pagamentos ou SQL não aplicado (supabase/plataforma.sql).</Notice>}
            <div className="grid gap-4 sm:grid-cols-4">
                <Stat label="Recebido" value={money(sum("payment"))} testid="payments-received" />
                <Stat label="Estornado" value={money(sum("refund"))} testid="payments-refunded" />
                <Stat label="Líquido" value={money(sum("payment") - sum("refund"))} testid="payments-net" />
                <Stat label="Saldos pendentes" value={money(pending.reduce((s, b) => s + Number(b.total) - Number(b.paid_amount), 0))} testid="payments-pending" />
            </div>
            <div className="flex flex-wrap gap-2">
                <button className={smallBtn} onClick={exportPayments} data-testid="payments-export">Exportar pagamentos (CSV)</button>
                <button className={smallBtn} onClick={exportBookings} data-testid="bookings-export">Exportar reservas (CSV)</button>
            </div>
            <Card className="space-y-2">
                <SubTitle>Saldos pendentes</SubTitle>
                {pending.map((b) => <p key={b.id} className="text-sm text-dim">{b.code} · {b.customer_name} · {STATUS[b.status]} · falta {money(b.total - b.paid_amount)}</p>)}
                {!pending.length && <p className="text-xs text-dim">Nenhum saldo pendente.</p>}
            </Card>
            <Card className="space-y-2">
                <SubTitle>Movimentações</SubTitle>
                {rows.map((p) => <p key={p.id} className="text-sm text-dim">{fmtDateTime(p.created_at)} · {byId[p.booking_id]?.code} · {p.kind === "payment" ? "Pagamento" : "Estorno"} {p.method} · <strong>{money(p.amount)}</strong> {p.note}</p>)}
                {!rows.length && <p className="text-xs text-dim">Nenhuma movimentação registrada.</p>}
            </Card>
        </div>
    );
}
